/**
 * Button — 项目统一按钮出口。
 *
 * 本体就是 antd Button（props 全透传，形态不变），只把入口收到项目里：以后要调默认
 * 尺寸、换底层实现或加埋点，只动这一个文件。固定形态的变体挂成子组件，如 Button.Text。
 *
 * antd 的 Button.Group 已被 Space.Compact 取代，这里不再转出。
 */
import {
  Button as AntdButton,
  type ButtonProps as AntdButtonProps,
} from "antd";
import type { Ref } from "react";

import styles from "./index.module.scss";

export type ButtonProps = AntdButtonProps & {
  ref?: Ref<HTMLAnchorElement | HTMLButtonElement>;
};

/**
 * 文字按钮的语义类型：常规操作 / 危险操作（删除、驳回）/ 警示操作（终止服务）。
 *
 * 与 antd 的 `type`（primary / link / text…）不是一回事：文字按钮的形态是固定的，
 * 这里的 type 只决定颜色，antd 那个 type 已被 Omit 掉，不会同时出现。
 */
export type TextButtonType = "primary" | "danger" | "warning";

/*
 * warning 用 antd 的 orange（主题里已把 orange 系列固定为 #F77234）。
 * 注意别改成 danger 与 color 混着传：antd 里 color + variant 优先于 danger 语法糖，
 * 两个一起传时 danger 只会留下一个没有样式的类名。
 */
const TYPE_COLOR: Record<TextButtonType, AntdButtonProps["color"]> = {
  primary: "primary",
  danger: "danger",
  warning: "orange",
};

export type TextButtonProps = Omit<
  ButtonProps,
  "type" | "variant" | "color" | "danger" | "size" | "shape" | "block" | "ghost"
> & {
  /** 默认 primary */
  type?: TextButtonType;
};

/**
 * 文字按钮：表格操作列（编辑 / 删除 / 通过 / 驳回 / 终止…）与行内轻操作。
 *
 * 颜色、hover、focus、disabled 全交给 antd（主题已按品牌色配过），CSS 只矫正几何尺寸，
 * 与 `.org-action-link`（styles/table.css）同口径，免得同一张表里两种按钮高矮不一。
 * 形态是固定的，所以不接受 variant / color / size，颜色走 type。
 */
function TextButton({ type = "primary", className, ...rest }: TextButtonProps) {
  return (
    <AntdButton
      {...rest}
      variant="text"
      color={TYPE_COLOR[type]}
      className={
        className
          ? `${styles["button-text"]} ${className}`
          : styles["button-text"]
      }
    />
  );
}

function Button({ ref, ...rest }: ButtonProps) {
  return <AntdButton ref={ref} {...rest} />;
}

Button.Text = TextButton;

export default Button;
